import { Link } from "@remix-run/react";
import { LineChart, Locate } from "lucide-react";

import { formatState } from "#app/utils/format-text.tsx";

import { GeneralErrorBoundary } from "./error-boundary.tsx";
import { Combobox } from "./forms.tsx";
import { Button } from "./ui/button.tsx";
import { Separator } from "./ui/separator.tsx";

export default function CitySuggestions({
  handleLocation,
  location,
  setLocation,
  setSearchParams,
  states,
  cities,
  groupedData,
}: any) {
  return (
    <main className="bg-gray-50 dark:bg-background">
      <div className="mx-auto flex w-full max-w-6xl p-6 py-20">
        <section className="flex w-full flex-col justify-center">
          <div className="flex flex-col pb-6">
            <h1 className="text-start text-2xl font-bold text-primary">
              Estações de carregamento
            </h1>
            <p className="text-lg text-muted-foreground">
              Encontre estações de carregamento para carros elétricos perto de
              você.
            </p>
          </div>
          <div className="flex flex-col items-start justify-start pb-16">
            <div className="flex w-full flex-col items-center space-y-4 md:w-2/3 md:flex-row md:space-x-2 md:space-y-0">
              <div id="state" className="flex w-full flex-col space-y-2">
                <Combobox
                  labelProps={{ children: "Estado" }}
                  inputProps={{ "aria-label": "Estado" }}
                  inputName="estado"
                  defaultData={location.estado || ""}
                  handleStateChange={(value) => {
                    const params = new URLSearchParams();
                    params.set("estado", value);
                    setSearchParams(params);
                    setLocation({
                      ...location,
                      estado: value,
                      cidade: undefined,
                      latitude: undefined,
                      longitude: undefined,
                    });
                  }}
                  showErrors={false}
                  items={states || []}
                />
              </div>
              <div id="city" className="flex w-full flex-col space-y-2">
                <Combobox
                  labelProps={{ children: "Cidade" }}
                  inputProps={{ "aria-label": "Cidade" }}
                  inputName="cidade"
                  defaultData={location.cidade || ""}
                  handleStateChange={(value) => {
                    setSearchParams(
                      (prev: { set: (arg0: string, arg1: string) => void }) => {
                        prev.set("cidade", value);
                        return prev;
                      },
                    );
                    setLocation({
                      ...location,
                      cidade: value,
                      latitude: undefined,
                      longitude: undefined,
                    });
                  }}
                  showErrors={false}
                  items={location.estado ? cities : []}
                />
              </div>
              <div className="flex w-full">
                <Button
                  className="animate-gradient-x flex w-full items-center justify-center gap-1 border-0 bg-gradient-to-r from-purple-600 via-pink-500 to-yellow-500 text-white hover:text-gray-200 hover:opacity-80 md:mt-6"
                  onClick={() => handleLocation()}
                  variant={"outline"}
                >
                  <Locate /> Estações próximas de mim
                </Button>
              </div>
            </div>
            <Link to="/dados-sobre-o-mercado-eletrico" className="-ml-4">
              <Button
                variant={"link"}
                className="w-full space-x-2 font-semibold text-[#FB8500]"
              >
                <LineChart />
                <span className="flex text-left md:hidden">
                  Dados sobre carregadores elétricos no Brasil
                </span>
                <span className="hidden md:flex">
                  Veja os últimos dados sobre a quantidade de carregadores de
                  carros elétricos no Brasil
                </span>
              </Button>
            </Link>
          </div>
          <Separator />
          <div className="grid auto-rows-max justify-items-stretch gap-6 pt-16 md:grid-cols-3">
            {Object.keys(groupedData).map((state: any) => (
              <div key={state}>
                <h2 className="text-xl font-bold text-primary">
                  {formatState(state)}
                </h2>
                <ul>
                  {groupedData[state].map((city: any) => (
                    <li
                      className="capitalize hover:text-primary hover:underline"
                      key={city.city + state}
                    >
                      <Link
                        prefetch="intent"
                        className="text-sm"
                        reloadDocument
                        to={`/estacoes?cidade=${encodeURIComponent(
                          city.city,
                        )}&estado=${encodeURIComponent(state)}`}
                      >
                        Estações de carregamento em {city.city}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}
